/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/sidebarMenu.scss';
import {IconName} from '@fortawesome/fontawesome-svg-core';
import classNames from 'classnames';

import React, {MouseEventHandler, ReactNode, useContext, useEffect, useRef, useState} from 'react';
import {NavLink, useLocation} from 'react-router';
import {AwesomeIcon, generateComponentId, isAbsoluteLink} from '../../../index';
import {ControlContext} from '../util/controlContext';
import {SidebarItem, SideMenuMode} from '../util/interfaces';

interface Props {
    onClickItem: (item: SidebarItem) => MouseEventHandler;
    items: SidebarItem[];
    activeItem?: string;
    className?: string;
    baseColor?: string;
    contrastColor?: string;
    controlled?: boolean;
    mode?: SideMenuMode;
    id?: string;
}

function getTopItems(items: SidebarItem[]): SidebarItem[] {
    return items.filter((item: SidebarItem) => !item.onBottom);
}

function getBottomItems(items: SidebarItem[]): SidebarItem[] {
    return items.filter((item: SidebarItem) => item.onBottom);
}

function getActiveItem(items: SidebarItem[], pathname: string): SidebarItem | null {
    for (const item of items) {
        if (itemIsActive(item, pathname)) return item;
        if (Array.isArray(item.children)) {
            const activeItem = getActiveItem(item.children, pathname);
            if (activeItem) return activeItem;
        }
    }
    return null;
}

function itemIsActive(item: SidebarItem, pathname: string): boolean {
    if (Array.isArray(item.route) && item.route.includes(pathname)) return true;
    else if (item.route === pathname) return true;
    else if (typeof item.route === 'string' && item.exactRoute === false && pathname.includes(item.route)) return true;
    return false;
}

function itemHasActiveId(item: SidebarItem, activeId: string): boolean {
    if (item.id === activeId) return true;
    else if (Array.isArray(item.children) && item.children.find(
        (child: SidebarItem) => itemHasActiveId(child, activeId))) return true;
    return false;
}

export default function SidebarMenu(props: Props) {
    const context = useContext(ControlContext);
    const location = useLocation();
    const {className, contrastColor, baseColor, items, onClickItem} = props;
    const mode = props.mode ?? SideMenuMode.COMPACT;
    const [activeItem, setActiveItem] = useState(props.activeItem ?? '');
    const sideBar = useRef<HTMLDivElement>(null);
    const componentId = useRef(generateComponentId(props.id));

    function onClickNavLink(item: SidebarItem) {
        return function (evt: React.MouseEvent) {
            if (!!onClickItem) {
                onClickItem(item)(evt);
                evt.preventDefault();
            }
        };
    }

    function _onClickItem(item: SidebarItem) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            const {route} = item;
            let _route: string | null = null;
            if (route) {
                if (Array.isArray(route) && route.length) _route = route[0];
                else _route = route as string;
            }
            if (_route && !isAbsoluteLink(_route))
                setActiveItem(item.id);
            onClickItem(item)(evt);
        };
    }

    function renderItem(isChild: boolean = false) {
        return function (item: SidebarItem) {
            const {className, icon, id, label, children, asNavLink} = item,
                isActive = itemHasActiveId(item, activeItem),
                cls = classNames(className, mode === SideMenuMode.COMPACT ? 'side-bar-item' : 'side-bar-item-full',
                    isChild && 'child', children !== undefined && 'with-children');

            const content = (active: boolean) => (
                <>
                    {renderIcon(isChild, label, active, icon)}
                    {mode === SideMenuMode.COMPACT &&
                        <div className={classNames('active-indicator', active && 'active')}/>}
                    <span className={classNames('item-label', active && 'active')}>{label}</span>
                    {children !== undefined && (
                        <>
                            {isActive && <div className='line'/>}
                            {renderChildren(isActive)(children)}
                        </>
                    )}
                </>
            );

            if (asNavLink) return (
                <NavLink key={id} to={item.route as string} className={cls} id={id}
                         onClick={onClickNavLink(item)}>
                    {({isActive}) => content(isActive)}
                </NavLink>
            );
            return (
                <div key={id} className={cls} onClick={_onClickItem(item)} id={id}>
                    {content(isActive)}
                </div>
            );
        };
    }

    function renderChildren(isActive: boolean) {
        return function (children: SidebarItem[]) {
            const display = isActive ? 'block' : 'none';
            return (
                <div className='children-container' style={{display}}>
                    {children.map(renderItem(true))}
                </div>
            );
        };
    }

    function renderIcon(isChild: boolean, label: string, active: boolean, icon?: IconName | ReactNode): ReactNode {
        const className = classNames('item-icon', active && 'active');
        if (icon) {
            if (typeof icon === 'string') return <AwesomeIcon name={icon as IconName} className={className}/>;
            else if (React.isValidElement(icon))
                return React.cloneElement((icon as React.ReactElement<any>),
                    {className: classNames(className, (icon.props as any).className)});
        }
        if (label.length) {
            if (isChild) return <span/>;
            return <span className={classNames(className, 'letter')}>{label[0]}</span>;
        }
        return null;
    }

    function setColors() {
        const bar = sideBar.current;
        if (bar) {
            const base: string = baseColor || context.baseColor,
                contrast: string = contrastColor || context.contrastColor;
            bar.style.setProperty('--base-color', base);
            bar.style.setProperty('--contrast-color', contrast);
        }
    }

    function _setActiveItem() {
        const {pathname} = location;
        const activeItem = getActiveItem(items, pathname);
        if (activeItem) setActiveItem(activeItem.id);
    }

    useEffect(() => {
        setColors();
        window.setTimeout(_setActiveItem);
    }, []);

    useEffect(() => {
        _setActiveItem();
    }, [location]);

    return (
        <div className={classNames(className, 'dvr-side-bar-menu', mode === SideMenuMode.FULL && 'full')}
             ref={sideBar} id={componentId.current}>
            <div>
                {getTopItems(items).map(renderItem())}
            </div>
            <div>
                {getBottomItems(items).map(renderItem())}
            </div>
        </div>
    );
}